import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  List,
  message,
  ConfigProvider,
  Empty,
  Tag,
  Button,
  Pagination,
} from "antd";
import {
  BellOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import "./notifications.css";
import { routes } from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    getNotifications();
  }, [pagination.current, pagination.pageSize]);

  const getNotifications = () => {
    let params = new URLSearchParams({
      page: pagination.current.toString(),
      limit: pagination.pageSize.toString(),
    });

    let getRes = (res) => {
      if (res.status === 200) {
        // Response structure: res.data.pageData
        const notificationsData = res.data?.data?.pageData || res.data?.pageData || [];
        setNotifications(notificationsData);
        // Total count is in res.size
        if (res.size !== undefined) {
          setPagination((prev) => ({
            ...prev,
            total: res.size || 0,
          }));
        }
      }
    };

    callApi(
      "GET",
      `${routes.getMyNotifications}?${params.toString()}`,
      null,
      setIsLoading,
      getRes,
      (error) => {
        message.error("Failed to load notifications");
      }
    );
  };

  const formatTime = (date) => {
    if (!date) return "N/A";
    return dayjs(date).fromNow();
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return dayjs(date).format("DD MMM YYYY, HH:mm A");
  };

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#FF66B2",
        },
      }}
    >
      <div className="notifications-container">
        <Loader loading={isLoading} />
        <div className="notifications-header">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ marginBottom: 16 }}
          >
            Back
          </Button>
          <h1 className="notifications-title">
            <BellOutlined /> Notifications
          </h1>
        </div>

        <div className="notifications-content">
          {notifications.length === 0 ? (
            <Empty description="No notifications found" />
          ) : (
            <>
              <List
                dataSource={notifications}
                renderItem={(item) => (
                  <List.Item className="notification-item">
                    <List.Item.Meta
                      avatar={
                        <Badge
                          dot={!item.isRead}
                          color="#FF66B2"
                        >
                          <BellOutlined
                            style={{
                              fontSize: 24,
                              color: "#FF66B2",
                            }}
                          />
                        </Badge>
                      }
                      title={
                        <div className="notification-title">
                          <span>{item.title || "Notification"}</span>
                          {item.notifyType && (
                            <Tag
                              color={
                                item.notifyType?.includes("moment")
                                  ? "blue"
                                  : item.notifyType?.includes("order")
                                  ? "green"
                                  : "default"
                              }
                            >
                              {item.notifyType}
                            </Tag>
                          )}
                        </div>
                      }
                      description={
                        <div className="notification-description">
                          <p>{item.message || item.description || "No description"}</p>
                          <div className="notification-time">
                            <span>{formatTime(item.createdAt)}</span>
                            <span className="notification-date">
                              {formatDate(item.createdAt)}
                            </span>
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
              {pagination.total > 0 && (
                <div className="notifications-pagination">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                    showSizeChanger
                    pageSizeOptions={["10", "20", "50", "100"]}
                    showTotal={(total) => `Total ${total} notifications`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Notifications;
